import React, { useState } from "react";
import { MapPin, Phone, Mail, Clock, CheckCircle2, Send } from "lucide-react";
import { useLang } from "@/context/LanguageContext";
import { Reveal, Kicker } from "@/components/site/Reveal";
import { BUSINESS } from "@/i18n/content";
import { api } from "@/lib/api";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

const empty = { name: "", email: "", phone: "", interest: "", message: "" };

export default function Contact() {
  const { tr } = useLang();
  const c = tr.contact;
  const [form, setForm] = useState(empty);
  const [errors, setErrors] = useState({});
  const [status, setStatus] = useState("idle"); // idle | sending | success | error

  const set = (k, v) => { setForm((f) => ({ ...f, [k]: v })); setErrors((e) => ({ ...e, [k]: "" })); };

  const validate = () => {
    const e = {};
    if (!form.name.trim()) e.name = true;
    if (!form.phone.trim()) e.phone = true;
    if (!form.interest) e.interest = true;
    if (!form.message.trim()) e.message = true;
    const emailOk = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email);
    if (!form.email.trim()) e.email = true;
    else if (!emailOk) e.email = "invalid";
    setErrors(e);
    return e;
  };

  const submit = async (ev) => {
    ev.preventDefault();
    const e = validate();
    if (Object.keys(e).length) return;
    setStatus("sending");
    try {
      await api.post("/contact", form);
      setStatus("success");
      setForm(empty);
    } catch {
      setStatus("error");
    }
  };

  const inputCls = (k) =>
    `w-full bg-white rounded-xl border px-4 py-3.5 outline-none transition-colors focus:ring-2 focus:ring-brand focus:border-transparent ${
      errors[k] ? "border-red-400" : "border-navy/10"
    }`;

  return (
    <div data-testid="contact-page">
      <section className="py-20 lg:py-28 bg-aqua/50">
        <div className="mx-auto max-w-4xl px-5 lg:px-8 text-center">
          <Reveal>
            <Kicker>{tr.nav.contact}</Kicker>
            <h1 className="font-heading font-extrabold text-navy text-4xl md:text-6xl tracking-tight leading-[1.05] mb-6">{c.heroTitle}</h1>
            <p className="text-lg md:text-xl text-ink/70 leading-relaxed max-w-2xl mx-auto">{c.heroSub}</p>
          </Reveal>
        </div>
      </section>

      <section className="py-20 lg:py-28">
        <div className="mx-auto max-w-7xl px-5 lg:px-8 grid lg:grid-cols-2 gap-12">
          {/* DETAILS */}
          <Reveal>
            <Kicker>{c.detailsTitle}</Kicker>
            <div className="space-y-4 mt-4 mb-10">
              <a href={BUSINESS.phoneHref} data-testid="contact-phone" className="flex items-center gap-4 bg-white rounded-2xl p-5 border border-navy/5 hover:border-brand/30 transition-colors">
                <span className="w-12 h-12 rounded-xl bg-aqua text-brand grid place-items-center"><Phone size={22} /></span>
                <span><span className="block text-xs uppercase tracking-widest text-ink/50 font-bold">{tr.common.phone}</span><span className="font-heading font-bold text-navy text-lg">{BUSINESS.phone}</span></span>
              </a>
              <a href={BUSINESS.emailHref} data-testid="contact-email" className="flex items-center gap-4 bg-white rounded-2xl p-5 border border-navy/5 hover:border-brand/30 transition-colors">
                <span className="w-12 h-12 rounded-xl bg-aqua text-brand grid place-items-center"><Mail size={22} /></span>
                <span><span className="block text-xs uppercase tracking-widest text-ink/50 font-bold">{tr.common.email}</span><span className="font-heading font-bold text-navy text-lg break-all">{BUSINESS.email}</span></span>
              </a>
              <div className="flex items-center gap-4 bg-white rounded-2xl p-5 border border-navy/5">
                <span className="w-12 h-12 rounded-xl bg-aqua text-brand grid place-items-center"><MapPin size={22} /></span>
                <span><span className="block text-xs uppercase tracking-widest text-ink/50 font-bold">{tr.common.address}</span><span className="font-heading font-bold text-navy">{BUSINESS.addressLine1}, {BUSINESS.addressLine2}</span></span>
              </div>
              <div className="flex items-center gap-4 bg-white rounded-2xl p-5 border border-navy/5">
                <span className="w-12 h-12 rounded-xl bg-aqua text-brand grid place-items-center"><Clock size={22} /></span>
                <span><span className="block text-xs uppercase tracking-widest text-ink/50 font-bold">{tr.common.hours}</span><span className="font-heading font-bold text-navy">{tr.common.byAppointment}</span></span>
              </div>
            </div>

            <p className="text-sm uppercase tracking-widest text-brand font-bold mb-3">{c.mapTitle}</p>
            <div className="rounded-3xl overflow-hidden border border-navy/10 shadow-lg aspect-[16/10]">
              <iframe
                title="Alma Y Lengua location map"
                data-testid="google-map"
                src={BUSINESS.mapEmbed}
                width="100%" height="100%" style={{ border: 0 }}
                allowFullScreen="" loading="lazy" referrerPolicy="no-referrer-when-downgrade"
              />
            </div>
          </Reveal>

          {/* FORM */}
          <Reveal delay={0.1}>
            <div className="bg-white rounded-[2rem] p-8 md:p-10 border border-navy/5 shadow-[0_20px_60px_rgba(7,20,63,0.08)]">
              {status === "success" ? (
                <div data-testid="contact-success" className="text-center py-10">
                  <div className="w-16 h-16 rounded-full bg-aqua text-brand grid place-items-center mx-auto mb-6"><CheckCircle2 size={34} /></div>
                  <h3 className="font-heading font-bold text-2xl text-navy mb-3">{c.formTitle}</h3>
                  <p className="text-ink/70 leading-relaxed">{c.success}</p>
                  <button onClick={() => setStatus("idle")} className="mt-8 inline-flex items-center gap-2 bg-brand text-white rounded-full px-6 py-3 font-semibold hover:bg-navy transition-colors">
                    {c.formTitle}
                  </button>
                </div>
              ) : (
                <form onSubmit={submit} data-testid="contact-form" noValidate>
                  <h3 className="font-heading font-extrabold text-2xl text-navy mb-6">{c.formTitle}</h3>
                  <div className="space-y-5">
                    <div>
                      <label htmlFor="name" className="block text-sm font-semibold text-navy mb-2">{c.name} *</label>
                      <input id="name" data-testid="input-name" value={form.name} onChange={(e) => set("name", e.target.value)} className={inputCls("name")} />
                    </div>
                    <div className="grid sm:grid-cols-2 gap-5">
                      <div>
                        <label htmlFor="email" className="block text-sm font-semibold text-navy mb-2">{c.email} *</label>
                        <input id="email" type="email" data-testid="input-email" value={form.email} onChange={(e) => set("email", e.target.value)} className={inputCls("email")} />
                        {errors.email === "invalid" && <p className="text-xs text-red-500 mt-1">{c.errorEmail}</p>}
                      </div>
                      <div>
                        <label htmlFor="phone" className="block text-sm font-semibold text-navy mb-2">{c.phone} *</label>
                        <input id="phone" type="tel" data-testid="input-phone" value={form.phone} onChange={(e) => set("phone", e.target.value)} className={inputCls("phone")} />
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-navy mb-2">{c.interest} *</label>
                      <Select value={form.interest} onValueChange={(v) => set("interest", v)}>
                        <SelectTrigger data-testid="select-interest" className={`h-auto py-3.5 rounded-xl bg-white ${errors.interest ? "border-red-400" : "border-navy/10"}`}>
                          <SelectValue placeholder={c.selectPlaceholder} />
                        </SelectTrigger>
                        <SelectContent>
                          {c.interests.map((opt) => (
                            <SelectItem key={opt} value={opt} data-testid={`interest-opt-${opt}`}>{opt}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <label htmlFor="message" className="block text-sm font-semibold text-navy mb-2">{c.message} *</label>
                      <textarea id="message" rows={5} data-testid="input-message" value={form.message} onChange={(e) => set("message", e.target.value)} className={inputCls("message")} />
                    </div>
                    {(Object.keys(errors).length > 0 && status !== "sending") && (
                      <p className="text-sm text-red-500" data-testid="form-error">{c.errorRequired}</p>
                    )}
                    {status === "error" && <p className="text-sm text-red-500">{c.errorGeneric}</p>}
                    <button type="submit" data-testid="contact-submit-button" disabled={status === "sending"}
                      className="w-full inline-flex items-center justify-center gap-2 bg-brand text-white rounded-full px-8 py-4 font-semibold hover:bg-navy transition-colors shadow-lg shadow-brand/20 disabled:opacity-60">
                      <Send size={18} /> {status === "sending" ? c.submitting : c.submit}
                    </button>
                  </div>
                </form>
              )}
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
